/* ===========================================================
   panels/TasksTab.jsx — the Updates doc's "Tell Claude" tab (his ask,
   2026-10-28)

   Write what Claude should do, from any device. The heartbeat on his
   computer (tools/claude-heartbeat.mjs) picks it up within 30 s and
   hands it to the open Claude Code chat, which marks it done with a
   reply. Each task shows where it is: waiting → on your computer →
   Claude has it → working → done. Data: worker/tasks.js.
   =========================================================== */

import { useEffect, useRef, useState } from 'preact/hooks';
import { api } from '../lib/api.js';
import { online } from '../state.js';
import { Icon, IconButton, Button, timeAgo, toast, confirmDialog } from '../ui/ui.jsx';
import { Proto } from '../ui/proto.jsx';

const STATUS = {
  new: ['Waiting for your computer', 'st-new'],
  delivered: ['On your computer', 'st-delivered'],
  seen: ['Claude has it', 'st-seen'],
  working: ['Claude is working on it', 'st-working'],
  done: ['Done', 'st-done'],
};
const BEAT_OK = 90_000;   // the heartbeat checks every 30 s; 3 misses = not listening

function Beat({ beat }) {
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick(x => x + 1), 5000); return () => clearInterval(t); }, []);
  const live = beat && Date.now() - beat.at < BEAT_OK;
  return (
    <div class={`tk-beat ${live ? 'live' : ''}`} role="status">
      <span class="tk-dot" />
      {live ? <span>Your computer is listening · checked {timeAgo(beat.at)}</span>
        : beat ? <span>Your computer last checked {timeAgo(beat.at)}. New tasks wait here until it's back on.</span>
          : <span>Your computer hasn't checked in yet. New tasks wait here until it does.</span>}
    </div>
  );
}

function Task({ t, reload }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(t.text);
  const [label, cls] = STATUS[t.status] || STATUS.new;
  const patch = async body => {
    try { await api(`/api/tasks/${t.id}`, { method: 'PATCH', body }); reload(); } catch (err) { toast(err, { kind: 'error' }); }
  };
  return (
    <li class={`tk ${t.status === 'done' ? 'done' : ''}`}>
      <button type="button" class={`tk-check ${t.status === 'done' ? 'on' : ''}`} aria-label={t.status === 'done' ? 'Reopen' : 'Mark done'}
        title={t.status === 'done' ? 'Reopen (Claude gets it again)' : 'Mark done yourself'}
        onClick={() => patch({ status: t.status === 'done' ? 'new' : 'done' })}>
        {t.status === 'done' && <Icon name="check" size={14} />}
      </button>
      <div class="tk-main">
        {editing ? (
          <form onSubmit={e => { e.preventDefault(); setEditing(false); if (text.trim() !== t.text) patch({ text }); }}>
            <textarea class="tk-edit" value={text} rows={3} autoFocus onInput={e => setText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) e.currentTarget.form.requestSubmit(); if (e.key === 'Escape') { setText(t.text); setEditing(false); } }} />
            <div class="tk-row"><Button size="sm" kind="primary" type="submit">Save</Button><Button size="sm" kind="ghost" onClick={() => { setText(t.text); setEditing(false); }}>Cancel</Button></div>
          </form>
        ) : <p class="tk-text">{t.text}</p>}
        <div class="tk-meta">
          <span class={`tk-chip ${cls}`}>{t.status === 'working' && <span class="tk-spin" />}{label}</span>
          <span>{t.status === 'done' && t.doneAt ? `done ${timeAgo(t.doneAt)}` : `sent ${timeAgo(t.createdAt)}`}</span>
          {t.by && <span class="tk-by">· {t.by}</span>}
        </div>
        {t.reply && <div class="tk-reply"><Icon name="sparkle" size={14} /><p>{t.reply}</p></div>}
      </div>
      <div class="tk-tools">
        {!editing && t.status !== 'done' && <IconButton icon="pencil" label="Edit" size={15} onClick={() => setEditing(true)} />}
        <IconButton icon="trash" label="Delete" size={15} onClick={async () => {
          if (t.status !== 'done' && !await confirmDialog({ title: 'Delete this task?', text: 'Claude won\'t do it.', ok: 'Delete', danger: true })) return;
          try { await api(`/api/tasks/${t.id}`, { method: 'DELETE' }); reload(); } catch (err) { toast(err, { kind: 'error' }); }
        }} />
      </div>
    </li>
  );
}

export function TasksTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const box = useRef();

  const reload = async () => {
    try { setData(await api('/api/tasks')); setErr(null); } catch (e) { setErr(e); }
  };
  useEffect(() => {
    reload();
    // keep the statuses moving while it's on screen
    const t = setInterval(() => { if (document.visibilityState === 'visible') reload(); }, 8000);
    window.addEventListener('online', reload);
    return () => { clearInterval(t); window.removeEventListener('online', reload); };
  }, []);

  const send = async () => {
    const v = text.trim();
    if (!v || sending) return;
    setSending(true);
    try {
      await api('/api/tasks', { method: 'POST', body: { text: v } });
      setText('');
      await reload();
      box.current?.focus();
    } catch (e) { toast(e, { kind: 'error' }); }
    setSending(false);
  };

  const tasks = data?.tasks || [];
  const open = tasks.filter(t => t.status !== 'done').sort((a, b) => a.createdAt - b.createdAt);
  const done = tasks.filter(t => t.status === 'done').sort((a, b) => (b.doneAt || 0) - (a.doneAt || 0));

  return (
    <div class="tasks-tab">
      <h1 class="tk-title"><Icon name="sparkle" size={22} /> Tell Claude</h1>
      <p class="hint">Write what you want done, from any device. Your computer checks twice a minute and hands it to the open Claude chat, which marks it done and replies here.</p>
      <Beat beat={data?.beat} />
      <div class="tk-compose">
        <textarea ref={box} rows={3} placeholder="What should Claude do? (Ctrl+Enter to send)" value={text} maxLength={4000}
          onInput={e => setText(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); send(); } }} />
        <div class="tk-row">
          {!online.value && <span class="hint-inline">You're offline. Send it when you're back.</span>}
          <span class="grow" />
          <Button kind="primary" size="sm" icon="sparkle" disabled={!text.trim() || sending || !online.value} onClick={send}>{sending ? 'Sending…' : 'Send to Claude'}</Button>
        </div>
      </div>
      {err && !data && <div class="empty"><Proto size={56} /><p>Couldn't load your tasks. {String(err.message || err)}</p><Button size="sm" onClick={reload}>Try again</Button></div>}
      {data && !tasks.length && <div class="empty"><Proto size={56} /><p>No tasks yet. Your first one goes above.</p></div>}
      {open.length > 0 && <><h2 class="tk-h">To do <span>{open.length}</span></h2><ul class="tk-list">{open.map(t => <Task t={t} key={t.id} reload={reload} />)}</ul></>}
      {done.length > 0 && (
        <>
          <button type="button" class="tk-h tk-toggle" aria-expanded={showDone} onClick={() => setShowDone(!showDone)}>
            <Icon name="chevron" size={14} class={showDone ? 'open' : ''} /> Done <span>{done.length}</span>
          </button>
          {showDone && <ul class="tk-list">{done.map(t => <Task t={t} key={t.id} reload={reload} />)}</ul>}
        </>
      )}
    </div>
  );
}
